import React, { useState, useEffect } from 'react';
import authService from '../../services/authService';
import './Admin.css';

const CreditExpiration = ({ organizationId, teamId }) => {
  const [expiringCredits, setExpiringCredits] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [daysAhead, setDaysAhead] = useState(30);
  const [includeExpired, setIncludeExpired] = useState(false);
  
  // Policy form
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [policyForm, setPolicyForm] = useState({
    entityType: 'ORGANIZATION',
    expirationDays: 0,
    gracePeriodDays: 0,
    notifyDaysBefore: [],
    autoExtendOnUse: false,
    returnToParent: true,
    effectiveDate: new Date().toISOString().split('T')[0]
  });
  
  // Extend expiration modal
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [extendDays, setExtendDays] = useState(30);
  const [extending, setExtending] = useState(false);

  useEffect(() => {
    loadExpiringCredits();
    loadPolicies();
  }, [organizationId, teamId, daysAhead, includeExpired]);

  const loadExpiringCredits = async () => {
    setLoading(true);
    setError('');
    try {
      const t = authService.getToken();
      const params = new URLSearchParams({
        daysAhead: daysAhead.toString(),
        includeExpired: includeExpired.toString()
      });
      
      if (organizationId) params.append('organizationId', organizationId);
      if (teamId) params.append('teamId', teamId);

      const res = await fetch(`/api/credits/expiring?${params}`, {
        headers: t ? { Authorization: `Bearer ${t}` } : {}
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to load expiring credits');
      }

      const data = await res.json();
      setExpiringCredits(data.grouped_by_date || []);
    } catch (e) {
      setError(e.message || 'Failed to load expiring credits');
    } finally {
      setLoading(false);
    }
  };

  const loadPolicies = async () => {
    if (!organizationId) return;
    
    try {
      const t = authService.getToken();
      const res = await fetch(`/api/credits/expiration-policy?organizationId=${organizationId}`, {
        headers: t ? { Authorization: `Bearer ${t}` } : {}
      });

      if (res.ok) {
        const data = await res.json();
        setPolicies(data.policies || []);
      }
    } catch (e) {
      console.error('Failed to load policies:', e);
    }
  };

  const handleSavePolicy = async () => {
    if (!organizationId) return;
    
    setLoading(true);
    setError('');
    try {
      const t = authService.getToken();
      const body = {
        ...policyForm,
        organizationId,
        policyId: editingPolicy?.policyId || null,
        notifyDaysBefore: policyForm.notifyDaysBefore.filter(d => d > 0).sort((a, b) => b - a)
      };

      const res = await fetch('/api/credits/expiration-policy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(t ? { Authorization: `Bearer ${t}` } : {})
        },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to save policy');
      }

      setShowPolicyModal(false);
      setEditingPolicy(null);
      setPolicyForm({
        entityType: 'ORGANIZATION',
        expirationDays: 0,
        gracePeriodDays: 0,
        notifyDaysBefore: [],
        autoExtendOnUse: false,
        returnToParent: true,
        effectiveDate: new Date().toISOString().split('T')[0]
      });
      loadPolicies();
    } catch (e) {
      setError(e.message || 'Failed to save policy');
    } finally {
      setLoading(false);
    }
  };

  const handleExtendExpiration = async () => {
    if (selectedTransactions.length === 0 || extendDays <= 0) return;
    
    setExtending(true);
    setError('');
    try {
      const t = authService.getToken();
      const res = await fetch('/api/credits/extend-expiration', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(t ? { Authorization: `Bearer ${t}` } : {})
        },
        body: JSON.stringify({
          transactionIds: selectedTransactions,
          extendDays
        })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to extend expiration');
      }

      setShowExtendModal(false);
      setSelectedTransactions([]);
      setExtendDays(30);
      loadExpiringCredits();
    } catch (e) {
      setError(e.message || 'Failed to extend expiration');
    } finally {
      setExtending(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toFixed(2)}`;
  };

  const getDaysUntilExpirationColor = (days) => {
    if (days < 0) return '#dc2626'; // Expired
    if (days <= 7) return '#f59e0b'; // Warning
    if (days <= 30) return '#3b82f6'; // Caution
    return '#059669'; // OK
  };

  // Flatten transactions from grouped data
  const allTransactions = expiringCredits.flatMap(group => 
    group.transactions.map(t => ({ ...t, expirationDate: group.expirationDate }))
  );

  return (
    <div className="admin-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 className="admin-header">Credit Expiration Management</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => {
              setEditingPolicy(null);
              setPolicyForm({
                entityType: 'ORGANIZATION',
                expirationDays: 0,
                gracePeriodDays: 0,
                notifyDaysBefore: [],
                autoExtendOnUse: false,
                returnToParent: true,
                effectiveDate: new Date().toISOString().split('T')[0]
              });
              setShowPolicyModal(true);
            }}
            style={{
              padding: '8px 16px',
              background: '#667eea',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 500
            }}
          >
            Configure Policy
          </button>
          {selectedTransactions.length > 0 && (
            <button
              onClick={() => setShowExtendModal(true)}
              style={{
                padding: '8px 16px',
                background: '#059669',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 500
              }}
            >
              Extend Selected ({selectedTransactions.length})
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-message" style={{ marginBottom: 12 }}>{error}</div>}

      {/* Policies Summary */}
      {policies.length > 0 && (
        <div style={{ marginBottom: 24, padding: 16, background: '#f9fafb', borderRadius: '8px' }}>
          <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: 16, fontWeight: 600 }}>Active Policies</h3>
          <div style={{ display: 'grid', gap: 12 }}>
            {policies.map(policy => (
              <div key={policy.policyId} style={{ padding: 12, background: '#fff', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      {policy.entityType} Policy
                    </div>
                    <div style={{ fontSize: 14, color: '#6b7280' }}>
                      Expiration: {policy.expirationDays === 0 ? 'Never' : `${policy.expirationDays} days`}
                      {policy.gracePeriodDays > 0 && ` • Grace Period: ${policy.gracePeriodDays} days`}
                      {policy.notifyDaysBefore && policy.notifyDaysBefore.length > 0 && (
                        ` • Notify: ${policy.notifyDaysBefore.join(', ')} days before`
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                      Effective: {formatDate(policy.effectiveDate)} • 
                      Auto-extend: {policy.autoExtendOnUse ? 'Yes' : 'No'} • 
                      Return to parent: {policy.returnToParent ? 'Yes' : 'No'}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setEditingPolicy(policy);
                      setPolicyForm({
                        entityType: policy.entityType,
                        expirationDays: policy.expirationDays,
                        gracePeriodDays: policy.gracePeriodDays,
                        notifyDaysBefore: policy.notifyDaysBefore || [],
                        autoExtendOnUse: policy.autoExtendOnUse,
                        returnToParent: policy.returnToParent,
                        effectiveDate: policy.effectiveDate ? new Date(policy.effectiveDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
                      });
                      setShowPolicyModal(true);
                    }}
                    style={{
                      padding: '6px 12px',
                      background: 'transparent',
                      color: '#667eea',
                      border: '1px solid #667eea',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: 12
                    }}
                  >
                    Edit
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="admin-controls" style={{ flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>Days Ahead:</span>
          <input
            type="number"
            value={daysAhead}
            onChange={e => setDaysAhead(Math.max(1, Number(e.target.value)))}
            min="1"
            max="365"
            style={{
              padding: '8px 12px',
              border: '1.5px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              background: '#fff',
              width: '100px'
            }}
          />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={includeExpired}
            onChange={e => setIncludeExpired(e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
          <span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>Include Expired</span>
        </label>
      </div>

      {/* Expiring Credits Summary */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Loading...</div>
      ) : expiringCredits.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
          No expiring credits found in the next {daysAhead} days
        </div>
      ) : (
        <>
          {/* Summary Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
            <div style={{ padding: 16, background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Total Expiring</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>
                {formatCurrency(expiringCredits.reduce((sum, g) => sum + g.totalAmount, 0))}
              </div>
            </div>
            <div style={{ padding: 16, background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Transaction Count</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>
                {expiringCredits.reduce((sum, g) => sum + g.transactionCount, 0)}
              </div>
            </div>
            <div style={{ padding: 16, background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Expiration Dates</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>
                {expiringCredits.length}
              </div>
            </div>
          </div>

          {/* Grouped by Date */}
          {expiringCredits.map((group, idx) => (
            <div key={idx} style={{ marginBottom: 24, padding: 16, background: '#fff', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                    {formatDate(group.expirationDate)}
                  </h3>
                  <div style={{ fontSize: 14, color: '#6b7280', marginTop: 4 }}>
                    {group.transactionCount} transaction{group.transactionCount !== 1 ? 's' : ''} • 
                    Total: {formatCurrency(group.totalAmount)}
                  </div>
                </div>
              </div>
              
              <div className="table-scroll">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>
                        <input
                          type="checkbox"
                          checked={group.transactions.every(t => selectedTransactions.includes(t.transactionId))}
                          onChange={e => {
                            if (e.target.checked) {
                              setSelectedTransactions([...selectedTransactions, ...group.transactions.map(t => t.transactionId)]);
                            } else {
                              setSelectedTransactions(selectedTransactions.filter(id => !group.transactions.some(t => t.transactionId === id)));
                            }
                          }}
                        />
                      </th>
                      <th>Amount</th>
                      <th>Days Until</th>
                      <th>Account</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.transactions.map((t) => {
                      const daysUntil = t.daysUntilExpiration;
                      return (
                        <tr key={t.transactionId}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedTransactions.includes(t.transactionId)}
                              onChange={e => {
                                if (e.target.checked) {
                                  setSelectedTransactions([...selectedTransactions, t.transactionId]);
                                } else {
                                  setSelectedTransactions(selectedTransactions.filter(id => id !== t.transactionId));
                                }
                              }}
                            />
                          </td>
                          <td style={{ fontWeight: 600 }}>{formatCurrency(t.amount)}</td>
                          <td>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: 12,
                              fontWeight: 500,
                              background: getDaysUntilExpirationColor(daysUntil) + '20',
                              color: getDaysUntilExpirationColor(daysUntil)
                            }}>
                              {daysUntil < 0 ? `Expired ${Math.abs(daysUntil)} days ago` : `${daysUntil} days`}
                            </span>
                          </td>
                          <td>
                            {t.account ? (
                              <span style={{ fontSize: 12 }}>
                                {t.account.entityType} - {t.account.entityId}
                              </span>
                            ) : '-'}
                          </td>
                          <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {t.description || '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </>
      )}

      {/* Policy Configuration Modal */}
      {showPolicyModal && (
        <div className="modal-overlay" onClick={() => setShowPolicyModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <button className="modal-close" onClick={() => setShowPolicyModal(false)}>×</button>
            <h2 style={{ marginTop: 0, marginBottom: 20 }}>
              {editingPolicy ? 'Edit' : 'Create'} Expiration Policy
            </h2>

            <div style={{ display: 'grid', gap: 16 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 500 }}>Entity Type</span>
                <select
                  value={policyForm.entityType}
                  onChange={e => setPolicyForm({ ...policyForm, entityType: e.target.value })}
                  style={{
                    padding: '10px 12px',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  <option value="ORGANIZATION">Organization</option>
                  <option value="TEAM">Team</option>
                  <option value="USER">User</option>
                </select>
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 500 }}>Expiration Days (0 = never expire)</span>
                <input
                  type="number"
                  value={policyForm.expirationDays}
                  onChange={e => setPolicyForm({ ...policyForm, expirationDays: Math.max(0, Number(e.target.value)) })}
                  min="0"
                  style={{
                    padding: '10px 12px',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: '#fff'
                  }}
                />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 500 }}>Grace Period Days</span>
                <input
                  type="number"
                  value={policyForm.gracePeriodDays}
                  onChange={e => setPolicyForm({ ...policyForm, gracePeriodDays: Math.max(0, Number(e.target.value)) })}
                  min="0"
                  style={{
                    padding: '10px 12px',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: '#fff'
                  }}
                />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 500 }}>Notify Days Before (comma-separated, e.g., 7,3,1)</span>
                <input
                  type="text"
                  value={policyForm.notifyDaysBefore.join(',')}
                  onChange={e => {
                    const days = e.target.value.split(',').map(d => parseInt(d.trim())).filter(d => !isNaN(d) && d > 0);
                    setPolicyForm({ ...policyForm, notifyDaysBefore: days });
                  }}
                  placeholder="7, 3, 1"
                  style={{
                    padding: '10px 12px',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: '#fff'
                  }}
                />
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={policyForm.autoExtendOnUse}
                  onChange={e => setPolicyForm({ ...policyForm, autoExtendOnUse: e.target.checked })}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: 14, fontWeight: 500 }}>Auto-extend on use</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={policyForm.returnToParent}
                  onChange={e => setPolicyForm({ ...policyForm, returnToParent: e.target.checked })}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: 14, fontWeight: 500 }}>Return to parent on expiration</span>
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 500 }}>Effective Date</span>
                <input
                  type="date"
                  value={policyForm.effectiveDate}
                  onChange={e => setPolicyForm({ ...policyForm, effectiveDate: e.target.value })}
                  style={{
                    padding: '10px 12px',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: '#fff'
                  }}
                />
              </label>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button
                  onClick={() => setShowPolicyModal(false)}
                  style={{
                    padding: '10px 20px',
                    background: '#f3f4f6',
                    color: '#111827',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: 14
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePolicy}
                  disabled={loading}
                  style={{
                    padding: '10px 20px',
                    background: '#667eea',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: 14,
                    fontWeight: 500,
                    opacity: loading ? 0.5 : 1
                  }}
                >
                  {loading ? 'Saving...' : 'Save Policy'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Extend Expiration Modal */}
      {showExtendModal && (
        <div className="modal-overlay" onClick={() => setShowExtendModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <button className="modal-close" onClick={() => setShowExtendModal(false)}>×</button>
            <h2 style={{ marginTop: 0, marginBottom: 20 }}>Extend Expiration</h2>

            <div style={{ display: 'grid', gap: 16 }}>
              <div>
                <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 8 }}>
                  {selectedTransactions.length} transaction{selectedTransactions.length !== 1 ? 's' : ''} selected
                </div>
              </div>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 500 }}>Extend by (days)</span>
                <input
                  type="number"
                  value={extendDays}
                  onChange={e => setExtendDays(Math.max(1, Number(e.target.value)))}
                  min="1"
                  style={{
                    padding: '10px 12px',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: '#fff'
                  }}
                />
              </label>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button
                  onClick={() => setShowExtendModal(false)}
                  style={{
                    padding: '10px 20px',
                    background: '#f3f4f6',
                    color: '#111827',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: 14
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleExtendExpiration}
                  disabled={extending || extendDays <= 0}
                  style={{
                    padding: '10px 20px',
                    background: '#059669',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: extending || extendDays <= 0 ? 'not-allowed' : 'pointer',
                    fontSize: 14,
                    fontWeight: 500,
                    opacity: extending || extendDays <= 0 ? 0.5 : 1
                  }}
                >
                  {extending ? 'Extending...' : 'Extend Expiration'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditExpiration;

